import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Pulse, Warning, CheckCircle, Clock, Eye, FileText, ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GlassCard } from "@/components/glass/GlassCard";
import { PageShell } from "@/components/layout/AppLayout";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import api from "@/lib/api";
import { toast } from "sonner";
import SearchSortBar from "@/components/SearchSortBar";
import useSearchSort from "@/hooks/useSearchSort";
import { useNavigate } from "react-router-dom";

const STATUS_ORDER = ["breached", "at_risk", "pending", "on_track", "complete"];

export default function Monitoring() {
  const nav = useNavigate();
  const [obligations, setObligations] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = () => api.get("/obligations").then(({ data }) => setObligations(data));
  useEffect(() => { load(); }, []);

  const update = async (id, status) => {
    await api.patch(`/obligations/${id}`, { status });
    toast.success("Updated");
    load();
  };

  const openDetail = async (ob) => {
    setDetail({ obligation: ob, contract: null, analysis: null });
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/obligations/${ob.id}`);
      setDetail(data);
    } catch { toast.error("Could not load obligation details"); }
    finally { setDetailLoading(false); }
  };

  const grouped = STATUS_ORDER.reduce((acc, s) => { acc[s] = obligations.filter((o) => o.status === s); return acc; }, {});
  const s = useSearchSort(obligations, {
    searchKeys: ["title", "description", "owner"],
    sortOptions: [
      { value: "due_date", label: "Due date", defaultDir: "asc" },
      { value: "priority", label: "Priority", defaultDir: "desc" },
      { value: "status", label: "Status", defaultDir: "asc" },
      { value: "owner", label: "Owner", defaultDir: "asc" },
      { value: "title", label: "Name A→Z", defaultDir: "asc" },
    ],
    defaultSort: "due_date",
  });

  return (
    <PageShell title="AI Monitoring & Obligations" subtitle="Continuous SLA tracking and breach prediction">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {STATUS_ORDER.map((s) => {
          const count = grouped[s].length;
          const icon = {
            breached: <Warning size={18} weight="duotone" className="text-rose-400" />,
            at_risk: <Pulse size={18} weight="duotone" className="text-amber-400" />,
            pending: <Clock size={18} weight="duotone" className="text-[var(--text-2)]" />,
            on_track: <CheckCircle size={18} weight="duotone" className="text-emerald-400" />,
            complete: <CheckCircle size={18} weight="duotone" className="text-[var(--text-3)]" />,
          }[s];
          return (
            <GlassCard key={s} className="p-4">
              <div className="flex items-center gap-2 overline mb-2">{icon} {s.replace("_", " ")}</div>
              <div className="text-3xl font-semibold tracking-tighter" style={{ fontFamily: 'var(--font-heading)' }}>{count}</div>
            </GlassCard>
          );
        })}
      </div>

      <SearchSortBar controller={s} placeholder="Search obligations by title, description, or owner…" testIdPrefix="monitoring" />
      <GlassCard className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--surface-2)] border-b border-[var(--border)]">
              <tr>
                <th className="text-start p-4 font-semibold overline">Obligation</th>
                <th className="text-start p-4 font-semibold overline">Owner</th>
                <th className="text-start p-4 font-semibold overline">Due</th>
                <th className="text-start p-4 font-semibold overline">Priority</th>
                <th className="text-start p-4 font-semibold overline">Status</th>
                <th className="text-end p-4 font-semibold overline">Details</th>
              </tr>
            </thead>
            <tbody>
              {s.items.map((o, i) => (
                <motion.tr
                  key={o.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.02 }}
                  className="border-b border-[var(--border)] hover:bg-[var(--surface-2)] transition"
                  data-testid={`obligation-row-${o.id}`}
                >
                  <td className="p-4">
                    <div className="font-medium">{o.title}</div>
                    <div className="text-xs text-[var(--text-3)]">{o.description}</div>
                  </td>
                  <td className="p-4 text-[var(--text-2)]">{o.owner}</td>
                  <td className="p-4 font-mono text-xs text-[var(--text-2)]">{o.due_date ? new Date(o.due_date).toLocaleDateString() : "—"}</td>
                  <td className="p-4"><span className={`chip chip-${o.priority === "critical" ? "critical" : o.priority === "high" ? "high" : o.priority === "medium" ? "medium" : "low"}`}>{o.priority}</span></td>
                  <td className="p-4">
                    <Select value={o.status} onValueChange={(v) => update(o.id, v)}>
                      <SelectTrigger data-testid={`obligation-status-${o.id}`} className="w-36 h-8 glass-subtle border-[var(--border)]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-4 text-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openDetail(o)}
                      data-testid={`obligation-view-${o.id}`}
                      className="gap-1.5 text-[var(--text-2)] hover:text-[var(--text)]"
                    >
                      <Eye size={14} /> View
                    </Button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
          {s.items.length === 0 && <div className="text-center text-[var(--text-3)] py-12 text-sm">{obligations.length ? "No matches." : "No obligations tracked yet."}</div>}
        </div>
      </GlassCard>

      <Sheet open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="obligation-detail-sheet">
          {detail && (
            <>
              <SheetHeader>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`chip chip-${detail.obligation.priority === "critical" ? "critical" : detail.obligation.priority === "high" ? "high" : detail.obligation.priority === "medium" ? "medium" : "low"}`}>
                    {detail.obligation.priority || "low"}
                  </span>
                  <span className="chip chip-neutral">{(detail.obligation.status || "pending").replace("_", " ")}</span>
                </div>
                <SheetTitle data-testid="obligation-detail-title" className="text-xl leading-tight">{detail.obligation.title}</SheetTitle>
                <SheetDescription className="text-sm text-[var(--text-2)] leading-relaxed whitespace-pre-wrap">
                  {detail.obligation.description || "No description recorded."}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <GlassCard className="p-3">
                  <div className="overline mb-1">Owner</div>
                  <div className="text-sm font-medium">{detail.obligation.owner || "—"}</div>
                </GlassCard>
                <GlassCard className="p-3">
                  <div className="overline mb-1">Due</div>
                  <div className="text-sm font-mono">{detail.obligation.due_date ? new Date(detail.obligation.due_date).toLocaleDateString() : "—"}</div>
                </GlassCard>
                {detail.obligation.frequency && (
                  <GlassCard className="p-3">
                    <div className="overline mb-1">Frequency</div>
                    <div className="text-sm">{detail.obligation.frequency}</div>
                  </GlassCard>
                )}
                {detail.obligation.category && (
                  <GlassCard className="p-3">
                    <div className="overline mb-1">Category</div>
                    <div className="text-sm">{detail.obligation.category}</div>
                  </GlassCard>
                )}
              </div>

              {detail.contract && (
                <GlassCard className="mt-5 p-4" data-testid="obligation-detail-contract">
                  <div className="flex items-center gap-2 mb-2 overline"><FileText size={13} /> Linked contract</div>
                  <div className="text-sm font-semibold">{detail.contract.title}</div>
                  <div className="text-xs text-[var(--text-2)] mt-0.5">{detail.contract.counterparty} · {detail.contract.status} · {detail.contract.category || "—"}</div>
                  {detail.contract.value_amount ? (
                    <div className="text-xs text-[var(--text-3)] mt-1 font-mono">
                      {new Intl.NumberFormat("en-US", { style: "currency", currency: detail.contract.value_currency || "USD", maximumFractionDigits: 0 }).format(detail.contract.value_amount)}
                    </div>
                  ) : null}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => nav(`/app/repository/${detail.contract.id}`)}
                    data-testid="obligation-detail-open-contract"
                    className="mt-3 gap-2"
                  >
                    Open contract <ArrowRight size={13} />
                  </Button>
                </GlassCard>
              )}

              {detail.analysis?.matched_clause && (
                <GlassCard className="mt-4 p-4">
                  <div className="overline mb-2">Related clause (from AI analysis)</div>
                  <div className="text-sm font-medium">{detail.analysis.matched_clause.title || "Clause"}</div>
                  <div className="text-xs text-[var(--text-2)] mt-1 leading-relaxed line-clamp-6 whitespace-pre-wrap">
                    {detail.analysis.matched_clause.text}
                  </div>
                  {typeof detail.analysis.risk_score === "number" && (
                    <div className="text-[10px] font-mono text-[var(--text-3)] mt-2">Risk score: {detail.analysis.risk_score}/100 · {detail.analysis.overall_stance}</div>
                  )}
                </GlassCard>
              )}

              {detailLoading && <div className="mt-4 text-xs text-[var(--text-3)]">Loading details…</div>}
              {!detailLoading && !detail.contract && (
                <div className="mt-4 text-xs text-[var(--text-3)]">No linked contract for this obligation.</div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>
    </PageShell>
  );
}
